import React, { useState, useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Expander from '@amzn/meridian/expander';
import { VictoryChart, VictoryAxis, VictoryArea, VictoryVoronoiContainer, VictoryLine } from 'victory';
import Legend, { LegendProvider } from '@amzn/meridian/legend';
import victoryLinePlugin from '@amzn/meridian/legend/plugin/victory-line';
import victoryTooltipPlugin from '@amzn/meridian/legend/plugin/victory-tooltip';
import useVictoryTheme from '@amzn/meridian/use-victory-theme';

import {
  CHART_WIDTH_RATIO,
  CHART_COLORS,
  buildXDomain,
  buildTimeScale,
  transformChartData,
  buildIntervalLines,
  formatTooltipTitle,
  renderIntervalLines
} from './chartUtils';

const VolumeChart = ({ volumeChartData, intervals }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const graphTheme = useVictoryTheme({ showIndependentGrid: false, interpolation: 'linear' });
  const { cumulativeInduct, cumulativeStow, cumulativePick, categories } = volumeChartData || {};
  const { minutesMap, tickValues, tickFormat } = useMemo(() => buildTimeScale(categories), [categories]);
  const intervalLines = useMemo(() => buildIntervalLines(intervals, minutesMap), [intervals, minutesMap]);

  const yMax = useMemo(() => {
    const allVol = [...(cumulativeInduct || []), ...(cumulativeStow || []), ...(cumulativePick || [])].map((d) => d.y);
    return allVol.length ? Math.max(...allVol) * 1.1 : 1000;
  }, [cumulativeInduct, cumulativeStow, cumulativePick]);

  const graphData = useMemo(
    () =>
      [
        {
          key: 'cumulativeInduct',
          label: 'Cumulative Induct Volume',
          color: CHART_COLORS.induct,
          data: transformChartData(cumulativeInduct, minutesMap)
        },
        {
          key: 'cumulativeStow',
          label: 'Cumulative Stow Volume',
          color: CHART_COLORS.stow,
          data: transformChartData(cumulativeStow, minutesMap)
        },
        {
          key: 'cumulativePick',
          label: 'Cumulative Pick Volume',
          color: CHART_COLORS.pick,
          data: transformChartData(cumulativePick, minutesMap)
        }
      ].filter(({ data }) => data?.length),
    [cumulativeInduct, cumulativeStow, cumulativePick, minutesMap]
  );

  const xDomain = useMemo(() => buildXDomain(tickValues), [tickValues]);

  if (!graphData.length) return null;

  return (
    <Column spacing="300" alignmentHorizontal="stretch" width="100%">
      <Expander open={isExpanded} onChange={setIsExpanded} title="Cumulative Volume" type="inline" spacingInset="300">
        <LegendProvider
          data={graphData}
          plugins={[victoryLinePlugin({ theme: graphTheme }), victoryTooltipPlugin({ showCrosshair: true })]}>
          {({ getLineProps, voronoiContainerProps }) => (
            <Column spacing="none">
              <VictoryChart
                theme={graphTheme}
                width={window.innerWidth * CHART_WIDTH_RATIO}
                height={380}
                padding={{ top: 40, left: 70, right: 0, bottom: 50 }}
                domain={{ x: xDomain }}
                containerComponent={
                  <VictoryVoronoiContainer
                    {...voronoiContainerProps}
                    labelComponent={formatTooltipTitle(voronoiContainerProps.labelComponent, tickFormat)}
                    labels={({ datum }) => (datum.y == null || isNaN(datum.y) ? '-' : `${Math.round(datum.y)}`)}
                  />
                }>
                <VictoryAxis
                  dependentAxis
                  style={{
                    tickLabels: { fontSize: 10, fill: '#5a6470' },
                    axisLabel: { fontSize: 11, fill: '#5a6470', padding: 55 }
                  }}
                  label="Cumulative Volume"
                  domain={[0, yMax]}
                />
                <VictoryAxis
                  tickValues={tickValues}
                  tickFormat={tickFormat}
                  style={{
                    tickLabels: { fontSize: 10, fill: '#5a6470', angle: 45, padding: 15 },
                    axisLabel: { fontSize: 11, fill: '#5a6470', padding: 35 }
                  }}
                  label="Time"
                  fixLabelOverlap
                />
                {graphData.map(({ key, data }) => {
                  const lineStyle = getLineProps(key).style;
                  return (
                    <VictoryArea
                      key={`area-${key}`}
                      data={data}
                      x="x"
                      y="y"
                      style={{
                        data: {
                          stroke: lineStyle?.data?.stroke,
                          strokeWidth: lineStyle?.data?.strokeWidth || 2,
                          fill: lineStyle?.data?.stroke,
                          fillOpacity: 0.15
                        }
                      }}
                    />
                  );
                })}
                {graphData.map(({ key, data }) => (
                  <VictoryLine
                    key={key}
                    data={data}
                    x="x"
                    y="y"
                    style={{
                      ...getLineProps(key).style,
                      data: { ...getLineProps(key).style?.data, stroke: 'transparent', strokeWidth: 0 }
                    }}
                  />
                ))}
                {renderIntervalLines(intervalLines, yMax)}
              </VictoryChart>
              <Legend direction="horizontal" />
            </Column>
          )}
        </LegendProvider>
      </Expander>
    </Column>
  );
};

export default VolumeChart;
